import { z } from 'zod';
import { TaxabilityReasonSchema } from '@/utils/sales-tax/types';

/**
 * The tax line as it travels to the client: on the product page's shipping
 * estimate, on every checkout shipping rate, and on the PaymentIntent
 * metadata. Small on purpose. The full quote's per-line detail stays
 * server-side; the page needs a number, a label and an honest flag that the
 * number is an estimate.
 *
 * Lives in schemas/ rather than beside the rate tables so the client bundle
 * that validates a rates response never pulls the tables in with it.
 */
export const TaxEstimateSchema = z.object({
  /** What is added to the charge, in cents. */
  taxCents: z.number().int().min(0),
  /** Effective rate on the taxable base, as a percentage (8.25 means 8.25%). */
  ratePercent: z.number().min(0),
  /** Receipt label: "Sales tax", "HST", "GST + PST". */
  label: z.string(),
  /** 'US-WA', 'CA-ON', or '' when nothing was quoted. */
  jurisdiction: z.string(),
  reason: TaxabilityReasonSchema,
  /**
   * True when the amount came from the in-repo table, whose local rate is a
   * state average rather than the buyer's street. The receipt says "estimated"
   * when this is set.
   */
  approximate: z.boolean(),
  provider: z.enum(['table', 'stripe', 'none']),
  /** Provider handle: the Stripe calculation id, or the table's content hash. */
  calculationId: z.string(),
  /**
   * The taxing jurisdictions the figure is made of, encoded by
   * utils/sales-tax/breakdown.ts, or '' where it is made of nothing filable.
   *
   * This is the one field here the page has no use for. It rides the estimate
   * because the estimate is what becomes the PaymentIntent metadata, and the
   * metadata is the only thing that survives from the quote to the sale -- and
   * a return is filed per jurisdiction, so a sale recording only its state is
   * a sale nobody can file.
   */
  breakdown: z.string().max(500).default(''),
});

export type TaxEstimate = z.infer<typeof TaxEstimateSchema>;
